import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import InputField from "../../../../Components/Masters/InputField";
import SelectField from "../../../../Components/Masters/SelectField";
import EditDialog from "../../../../Components/Common/EditDialog";
import { post, put } from "../../../../utils/api";
import countries from "../../Roles and Permissions/Country.json";

/** API may return `productName` as `{ _id }`, or `[{ _id, productName }]`. SelectField value must be product `_id`. */
function getProductMasterIdFromElement(productNameField) {
  if (productNameField == null || productNameField === "") return "";
  if (Array.isArray(productNameField)) {
    if (productNameField.length === 0) return "";
    const first = productNameField[0];
    if (first && typeof first === "object") {
      return first._id || first.id || "";
    }
    return String(first);
  }
  if (typeof productNameField === "object") {
    return productNameField._id || productNameField.id || "";
  }
  return String(productNameField);
}

function AddCustomerMaster({
  formInputData,
  fetchData,
  id,
  element,
  setAddData,
  addData,
  setsnackOpen,
  addUrl,
  updateUrl,
  /** Optional: called after successful save with submitted payload (before fetchData). */
  onAfterSave,
  /** When true, render inline (e.g. bulk upload Collapse) instead of right Drawer. */
  embedded = false,
  embeddedTitle,
  /** Optional sx override for embedded form fields grid (e.g. 2 columns). */
  embeddedFieldsContainerSx,
  /** Optional: sx merged onto each field's root container, keyed by `fieldName`. */
  fieldContainerSxByFieldName = {},
  /** Optional: `fieldName`s that stay disabled; values still come from `element` / form state. */
  readOnlyFieldNames = [],
  /** Optional: sx merged onto embedded form root. */
  embeddedRootSx = {},
  /** Optional: sx merged onto the embedded Submit button container. */
  submitButtonContainerSx = {},
  /** Optional: sx merged onto the embedded Submit button. */
  submitButtonSx = {},
}) {
  const [errors, setErrors] = useState({});
  const [editDialog, setEditDialog] = useState(false);
  const [processingMethodDraft, setProcessingMethodDraft] = useState("");

  const buildInitialState = () => ({
    customerVendorName: element?.customerVendorName || "",
    productName: getProductMasterIdFromElement(element?.productName),
    picName: element?.picName || "",
    country: element?.country || "",
    email: element?.email || "",
    processingMethodsUsed: Array.isArray(element?.processingMethodsUsed)
      ? element.processingMethodsUsed
      : element?.processingMethodsUsed
        ? [element.processingMethodsUsed]
        : [],
  });

  const [formData, setFormData] = useState(buildInitialState);
  const [formData2, setFormData2] = useState(buildInitialState);

  useEffect(() => {
    const next = buildInitialState();
    setFormData(next);
    setFormData2(next);
    setProcessingMethodDraft("");
  }, [element, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleAddProcessingMethod = () => {
    const t = processingMethodDraft.trim();
    if (!t) return;
    const cur = Array.isArray(formData.processingMethodsUsed)
      ? formData.processingMethodsUsed
      : [];
    const exists = cur.some((m) => String(m).trim().toLowerCase() === t.toLowerCase());
    if (exists) {
      setProcessingMethodDraft("");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      processingMethodsUsed: [
        ...(Array.isArray(prev.processingMethodsUsed) ? prev.processingMethodsUsed : []),
        t,
      ],
    }));
    setProcessingMethodDraft("");
    setErrors((prev) => ({ ...prev, processingMethodsUsed: "" }));
  };

  const handleRemoveProcessingMethod = (method) => {
    setFormData((prev) => ({
      ...prev,
      processingMethodsUsed: (prev.processingMethodsUsed || []).filter((m) => m !== method),
    }));
  };

  const handleExitAgree = () => {
    setEditDialog(false);
    setAddData(false);
    setErrors({});
    const cleared = buildInitialState();
    setFormData(cleared);
    setFormData2(cleared);
    setProcessingMethodDraft("");
  };

  const handleClose = () => {
    const unchanged = Object.keys(formData).every((key) => formData[key] === formData2[key]);
    if (unchanged) handleExitAgree();
    else setEditDialog(true);
  };

  const validateForm = () => {
    const next = {};
    formInputData.forEach((input) => {
      const value = formData[input.fieldName];
      if (
        (Array.isArray(value) && value.length === 0) ||
        (!Array.isArray(value) && (!value || String(value).trim() === ""))
      ) {
        next[input.fieldName] = `${input.title} is required`;
      }
    });

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      next.email = "Enter a valid email";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const Save = async () => {
    if (!validateForm()) return;

    const payload = {
      customerVendorName: formData.customerVendorName.trim(),
      productName: formData.productName,
      picName: formData.picName.trim(),
      country: formData.country,
      email: formData.email.trim(),
      processingMethodsUsed: formData.processingMethodsUsed,
    };

    const request = id ? put(`${updateUrl}?Id=${id}`, payload) : post(addUrl, payload);

    await request
      .then((res) => {
        setsnackOpen({
          open: true,
          message: res.data.message,
          severity: "success",
        });
        if (typeof onAfterSave === "function") {
          onAfterSave({ payload, data: res?.data });
        }
        fetchData();
        handleExitAgree();
      })
      .catch((err) => {
        const apiMessage = err?.response?.data?.message || "Something went wrong";
        setsnackOpen({
          open: true,
          message: apiMessage,
          severity: "error",
        });
      });
  };

  const isFieldReadOnly = (fieldName) =>
    Array.isArray(readOnlyFieldNames) && readOnlyFieldNames.includes(fieldName);

  const renderField = (el) => {
    if (el.fieldName === "productName") {
      const readOnly = isFieldReadOnly(el.fieldName);
      return (
        <SelectField
          key={el.fieldName}
          title={el.title}
          name={el.fieldName}
          value={formData.productName}
          handleChange={handleChange}
          url="/master/product/productsList"
          field="productName"
          errors={errors[el.fieldName]}
          disable={readOnly}
          disabledGrey={readOnly}
        />
      );
    }
    if (el.fieldName === "country") {
      return (
        <SelectField
          key={el.fieldName}
          title={el.title}
          name={el.fieldName}
          value={formData.country}
          handleChange={handleChange}
          options={countries.countries}
          field="name"
          errors={errors[el.fieldName]}
        />
      );
    }
    if (el.fieldName === "processingMethodsUsed") {
      return (
        <Box
          key={el.fieldName}
          sx={{
            mt: 1,
            ...(embedded
              ? {
                  gridColumn: "1 / -1",
                  width: "100%",
                  minWidth: 0,
                }
              : {}),
            ...(fieldContainerSxByFieldName[el.fieldName] || {}),
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: embedded ? 0 : 1 }}>
            {el.title}
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              mt: 1,
              flexWrap: embedded ? "nowrap" : "wrap",
              width: embedded ? "100%" : undefined,
              maxWidth: embedded ? "100%" : undefined,
            }}
          >
            <TextField
              size="small"
              fullWidth
              sx={{
                flex: embedded ? "1 1 auto" : "1 1 200px",
                minWidth: 0,
                "& .MuiOutlinedInput-root": {
                  height: 40,
                },
              }}
              placeholder={el.placeholder || "e.g. Recycling"}
              value={processingMethodDraft}
              onChange={(e) => setProcessingMethodDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddProcessingMethod();
                }
              }}
            />
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={handleAddProcessingMethod}
              sx={{
                textTransform: "none",
                flexShrink: 0,
                whiteSpace: "nowrap",
                px: 2,
                height: 40,
                minHeight: 40,
                boxSizing: "border-box",
              }}
            >
              {(formData.processingMethodsUsed || []).length > 0 ? "+ Add more" : "+ Add"}
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            Type a method, then click Add or press Enter. Use + Add more for additional methods.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1.5 }}>
            {(formData.processingMethodsUsed || []).map((value) => (
              <Chip
                key={value}
                label={value}
                sx={{
                  bgcolor: "#1C486B",
                  color: "white",
                  height: "28px",
                  m: 0.25,
                  "& .MuiChip-deleteIcon": {
                    color: "white",
                    "&:hover": {
                      color: "white",
                    },
                  },
                }}
                onDelete={() => handleRemoveProcessingMethod(value)}
              />
            ))}
          </Box>

          {errors[el.fieldName] && (
            <Typography variant="body2" color="#D34040" sx={{ mt: 0.5 }}>
              {errors[el.fieldName]}
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <InputField
        key={el.fieldName}
        title={el.title}
        handleChange={handleChange}
        placeholder={el.placeholder}
        value={formData[el.fieldName] || ""}
        errors={errors[el.fieldName]}
        fieldName={el.fieldName}
        disabled={isFieldReadOnly(el.fieldName)}
      />
    );
  };

  /** Inline bulk-upload form only — Drawer path below is unchanged from original. */
  if (embedded) {
    if (!addData) return null;
    return (
      <Box sx={{ width: "100%", ...(embeddedRootSx || {}) }}>
        {editDialog && <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />}
        <Typography variant="h6" sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}>
          {embeddedTitle || (id ? "Edit Customer" : "Add Customer")}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr",
            rowGap: 1.5,
            ...(embeddedFieldsContainerSx || {}),
          }}
        >
          {formInputData.map((el) => renderField(el))}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            ...(submitButtonContainerSx || {}),
          }}
        >
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#0a3f73",
              textTransform: "none",
              marginTop: "1rem",
              ...(submitButtonSx || {}),
            }}
            onClick={Save}
          >
            Submit
          </Button>
        </Box>
      </Box>
    );
  }

  const drawerList = () => (
    <Box display="flex" gap={3} sx={{ width: "28rem", maxWidth: "100%" }}>
      {editDialog && <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />}
      <Box
        sx={{
          backgroundColor: "#fff",
          height: "20px",
          padding: "13px",
          margin: "30px 0 0 0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "80%",
          width: "5%",
        }}
      >
        <IconButton onClick={handleClose} sx={{ color: "black" }}>
          <CloseIcon sx={{ fontSize: "1.2rem" }} />
        </IconButton>
      </Box>
      <Box
        sx={{
          width: "90%",
          p: 3,
          backgroundColor: "#fff",
          height: "100vh",
          overflowY: "scroll",
        }}
      >
        <Typography variant="h6" sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}>
          {id ? "Edit Customer" : "Add Customer"}
        </Typography>
        {formInputData.map((el) => renderField(el))}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#0a3f73",
              textTransform: "none",
              marginTop: "10%",
            }}
            onClick={Save}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Drawer
      sx={{
        backgroundColor: "transparent",
        height: "100vh",
        minHeight: "100vh",
        overflowY: "auto",
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      open={addData}
      onClose={handleClose}
    >
      {drawerList()}
    </Drawer>
  );
}

export default AddCustomerMaster;
